import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExternalLink, faTrash } from "@fortawesome/pro-regular-svg-icons";
import TextInput from "../../common/TextInput";
import TinyLoader from "../../loaders/TinyLoader";
import AppRoutes from "../../../routes/AppRoutes";
import analyticsAdminApi from "../../../api/analytics/analyticsAdminApi";
import type { AnalyticsDataPointMatchDto } from "../../../types/analytics/admin/AnalyticsAdminModels";
import {
  createAttachedDataPointKey,
  isDuplicateAttachment,
  previewNewDataPointName,
  type SimpleWidgetAttachedDataPoint,
} from "../../../helpers/analytics/simpleWidgetDataPoints";

interface SimpleWidgetDataPointsEditorProps {
  accessToken: string | undefined;
  rows: SimpleWidgetAttachedDataPoint[];
  onChange: (rows: SimpleWidgetAttachedDataPoint[]) => void;
  canEdit: boolean;
  showValidation?: boolean;
}

const SimpleWidgetDataPointsEditor = ({
  accessToken,
  rows,
  onChange,
  canEdit,
  showValidation = false,
}: SimpleWidgetDataPointsEditorProps) => {
  const { t } = useTranslation();
  const api = useMemo(() => new analyticsAdminApi(accessToken), [accessToken]);
  const [clientFormId, setClientFormId] = useState("");
  const [scoreRef, setScoreRef] = useState(rows[0]?.scoreRef ?? "");
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [addError, setAddError] = useState<string | null>(null);
  const [matches, setMatches] = useState<AnalyticsDataPointMatchDto[] | null>(
    null,
  );
  const [formName, setFormName] = useState<string | null>(null);
  const [selectedMatchId, setSelectedMatchId] = useState<number | "new">("new");

  const parsedFormId = Number(clientFormId);
  const isFormIdValid = clientFormId.trim().length > 0 && !isNaN(parsedFormId);
  const isScoreRefValid =
    scoreRef.trim().length > 0 && scoreRef.trim().length <= 100;
  const firstScoreRef = rows[0]?.scoreRef ?? "";

  const resetAddRow = () => {
    setClientFormId("");
    setScoreRef(firstScoreRef);
    setMatches(null);
    setFormName(null);
    setSelectedMatchId("new");
    setLookupError(null);
    setAddError(null);
  };

  const attachRow = (row: SimpleWidgetAttachedDataPoint) => {
    onChange([...rows, row]);
    resetAddRow();
  };

  const handleLookup = () => {
    if (!isFormIdValid || !isScoreRefValid) {
      setAddError(
        t(
          "Pages.Admin.AnalyticsPages.DataPoints.AddValidation",
          "Enter a client form ID and question tag.",
        ),
      );
      return;
    }

    setIsLookingUp(true);
    setLookupError(null);
    setAddError(null);
    api.findMatchingDataPoints(
      parsedFormId,
      scoreRef.trim(),
      (response) => {
        setIsLookingUp(false);
        setFormName(response.clientFormName);
        setMatches(response.matches || []);
        if (response.matches && response.matches.length > 0) {
          setSelectedMatchId(response.matches[0].id);
        } else {
          const tag = scoreRef.trim();
          if (isDuplicateAttachment(rows, parsedFormId, tag, null)) {
            setAddError(
              t(
                "Pages.Admin.AnalyticsPages.DataPoints.Duplicate",
                "This datapoint or form and tag pair is already attached.",
              ),
            );
            return;
          }

          attachRow({
            key: createAttachedDataPointKey(),
            dataPointId: null,
            adminName: previewNewDataPointName(
              response.clientFormName,
              parsedFormId,
              tag,
            ),
            clientFormId: parsedFormId,
            clientFormName: response.clientFormName,
            scoreRef: tag,
          });
        }
      },
      () => {
        setIsLookingUp(false);
        setLookupError(
          t(
            "Pages.Admin.AnalyticsPages.DataPoints.LookupError",
            "Could not look up existing datapoints.",
          ),
        );
      },
    );
  };

  const handleConfirmMatch = () => {
    if (!isFormIdValid || !isScoreRefValid) {
      return;
    }

    const tag = scoreRef.trim();
    const reuseId =
      selectedMatchId === "new" ? null : selectedMatchId;
    if (isDuplicateAttachment(rows, parsedFormId, tag, reuseId)) {
      setAddError(
        t(
          "Pages.Admin.AnalyticsPages.DataPoints.Duplicate",
          "This datapoint or form and tag pair is already attached.",
        ),
      );
      return;
    }

    if (reuseId) {
      const match = (matches || []).find((item) => item.id === reuseId);
      if (!match) {
        return;
      }
      attachRow({
        key: createAttachedDataPointKey(),
        dataPointId: match.id,
        adminName: match.adminName,
        clientFormId: match.clientFormId ?? parsedFormId,
        clientFormName: match.clientFormName || formName,
        scoreRef: match.scoreRef || tag,
      });
      return;
    }

    attachRow({
      key: createAttachedDataPointKey(),
      dataPointId: null,
      adminName: previewNewDataPointName(formName, parsedFormId, tag),
      clientFormId: parsedFormId,
      clientFormName: formName,
      scoreRef: tag,
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-semibold text-gray-700">
          {t("Pages.Admin.AnalyticsPages.DataPoints.Heading", "Datapoints")}
        </h3>
        <p className="mt-1 text-xs text-gray-500">
          {t(
            "Pages.Admin.AnalyticsPages.DataPoints.Help",
            "Attach one or more MultiChoice datapoints. Form and tag are read from each datapoint.",
          )}
        </p>
      </div>

      {rows.length === 0 && showValidation && (
        <p className="text-xs text-red-600">
          {t(
            "Pages.Admin.AnalyticsPages.DataPoints.Required",
            "Attach at least one datapoint.",
          )}
        </p>
      )}

      {rows.length > 0 && (
        <ul className="space-y-3">
          {rows.map((row) => (
            <li
              key={row.key}
              className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-1">
                  <p className="font-medium text-gray-900">
                    {row.adminName}
                    {row.dataPointId != null && (
                      <span className="ml-2 font-normal text-gray-500">
                        {t(
                          "Pages.Admin.AnalyticsPages.DataPoints.IdLabel",
                          "ID {{id}}",
                          { id: row.dataPointId },
                        )}
                      </span>
                    )}
                    {row.dataPointId == null && (
                      <span className="ml-2 font-normal text-gray-500">
                        {t(
                          "Pages.Admin.AnalyticsPages.DataPoints.WillCreate",
                          "Will create on save",
                        )}
                      </span>
                    )}
                  </p>
                  <p>
                    {t(
                      "Pages.Admin.AnalyticsPages.DataPoints.FormLabel",
                      "Form {{id}}{{name}}",
                      {
                        id: row.clientFormId,
                        name: row.clientFormName
                          ? ` (${row.clientFormName})`
                          : "",
                      },
                    )}
                  </p>
                  <p>
                    {t(
                      "Pages.Admin.AnalyticsPages.DataPoints.ScoreRefLabel",
                      "Score ref {{tag}}",
                      { tag: row.scoreRef },
                    )}
                  </p>
                </div>
                <div className="flex shrink-0 gap-2">
                  {row.dataPointId != null && (
                    <a
                      className="btn-secondary btn-small inline-flex items-center gap-1.5"
                      href={AppRoutes.admin.datapoints.manage.generateRoute(
                        row.dataPointId.toString(),
                      )}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <FontAwesomeIcon icon={faExternalLink} />
                      {t(
                        "Pages.Admin.AnalyticsPages.DataPoints.EditLink",
                        "Edit datapoint",
                      )}
                    </a>
                  )}
                  {canEdit && (
                    <button
                      type="button"
                      className="btn-secondary btn-small inline-flex items-center gap-1.5 text-red-700"
                      onClick={() =>
                        onChange(rows.filter((item) => item.key !== row.key))
                      }
                      aria-label={t("Common.Remove", "Remove")}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                      {t("Common.Remove", "Remove")}
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {canEdit && (
        <div className="space-y-3 rounded-md border border-dashed border-gray-300 p-3">
          <p className="text-sm font-medium text-gray-700">
            {t(
              "Pages.Admin.AnalyticsPages.DataPoints.AddHeading",
              "Add datapoint",
            )}
          </p>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label
                htmlFor="simple-widget-form-id"
                className="mb-1 block text-sm font-semibold text-gray-700"
              >
                {t(
                  "Pages.Admin.AnalyticsPages.CreateWidget.ClientFormIdLabel",
                  "Client form ID",
                )}
              </label>
              <TextInput
                inputId="simple-widget-form-id"
                value={clientFormId}
                onChange={setClientFormId}
                maxLength={20}
                className="w-full rounded-md border border-gray-300 px-2 py-1"
              />
            </div>
            <div>
              <label
                htmlFor="simple-widget-score-ref"
                className="mb-1 block text-sm font-semibold text-gray-700"
              >
                {t(
                  "Pages.Admin.AnalyticsPages.CreateWidget.QuestionTagLabel",
                  "Question tag",
                )}
              </label>
              <TextInput
                inputId="simple-widget-score-ref"
                value={scoreRef}
                onChange={setScoreRef}
                maxLength={100}
                className="w-full rounded-md border border-gray-300 px-2 py-1"
              />
            </div>
          </div>
          {matches && matches.length > 0 && (
            <div className="space-y-2 rounded-md bg-blue-50 p-3 text-sm text-gray-800">
              {matches.map((match) => (
                <label key={match.id} className="flex items-start gap-2">
                  <input
                    type="radio"
                    name="simple-widget-dp-reuse"
                    checked={selectedMatchId === match.id}
                    onChange={() => setSelectedMatchId(match.id)}
                    className="mt-1 border-gray-300 text-primary focus:ring-primary"
                  />
                  <span>
                    {t(
                      "Pages.Admin.AnalyticsPages.DataPoints.ReusePrompt",
                      "There's already a datapoint [id: {{id}}] called '{{name}}' - want to use that, or create a new data point?",
                      { id: match.id, name: match.adminName },
                    )}
                  </span>
                </label>
              ))}
              <label className="flex items-start gap-2">
                <input
                  type="radio"
                  name="simple-widget-dp-reuse"
                  checked={selectedMatchId === "new"}
                  onChange={() => setSelectedMatchId("new")}
                  className="mt-1 border-gray-300 text-primary focus:ring-primary"
                />
                <span>
                  {t(
                    "Pages.Admin.AnalyticsPages.DataPoints.CreateNew",
                    "Create a new datapoint",
                  )}
                </span>
              </label>
            </div>
          )}
          {lookupError && <p className="text-xs text-red-600">{lookupError}</p>}
          {addError && <p className="text-xs text-red-600">{addError}</p>}
          <div className="flex justify-end">
            <button
              type="button"
              className="btn-secondary btn-small inline-flex items-center gap-2"
              onClick={matches && matches.length > 0 ? handleConfirmMatch : handleLookup}
              disabled={isLookingUp}
            >
              {isLookingUp && <TinyLoader />}
              {matches && matches.length > 0
                ? t(
                    "Pages.Admin.AnalyticsPages.DataPoints.Attach",
                    "Attach datapoint",
                  )
                : t(
                    "Pages.Admin.AnalyticsPages.DataPoints.Add",
                    "Add datapoint",
                  )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SimpleWidgetDataPointsEditor;
